import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Database, AlertTriangle } from "lucide-react";

const fmtN = (n) => (n == null ? "—" : Number(n).toLocaleString("en-IN"));

export const QLDashboard = () => {
  const [data, setData] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const load = () => api.get("/quantlab/overview").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/quantlab/sync/underlying");
      const res = r.data.result || {};
      toast.success(Object.entries(res).map(([s, v]) => `${s}: +${v.inserted ?? 0}`).join(" · "));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    finally { setSyncing(false); }
  };

  if (!data) return <div className="text-zinc-600 font-mono text-xs py-10 text-center">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="ql-dashboard">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
          Underlying data coverage · last live sync {data.last_sync?.at ? new Date(data.last_sync.at).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata" }) + " IST" : "never"}
        </div>
        <button onClick={sync} disabled={syncing}
                className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-mono border border-zinc-700 px-4 py-2 text-zinc-300 hover:text-white disabled:opacity-50"
                data-testid="ql-sync-btn">
          <RefreshCw className={`h-3.5 w-3.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Pulling feed…" : "Sync underlying from live feed"}
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {(data.cards || []).map((c) => (
          <div key={c.symbol} className="border border-zinc-800 bg-[#0a0a0a] p-4 space-y-3" data-testid={`ql-card-${c.symbol}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-heading font-black text-lg">{c.symbol}</div>
                <div className="text-[10px] text-zinc-500 font-mono">{c.exchange} · lot {c.lot_size} · strikes ×{c.strike_interval} · {c.expiry_schedule} exp</div>
              </div>
              {c.last_price ? (
                <div className="text-right">
                  <div className="font-mono text-xl font-bold">{fmtN(c.last_price)}</div>
                  {c.day_change_pct != null && (
                    <div className={`text-xs font-mono ${c.day_change_pct >= 0 ? "text-[#22C55E]" : "text-red-400"}`}>
                      {c.day_change_pct >= 0 ? "+" : ""}{c.day_change_pct}% day
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-[10px] font-mono border border-[#F59E0B]/50 text-[#F59E0B] px-2 py-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> INSUFFICIENT DATA
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] font-mono">
              <span className="text-zinc-500">Coverage</span>
              <span className="text-zinc-200">{c.coverage_from ? `${c.coverage_from} → ${c.coverage_to}` : "—"}</span>
              <span className="text-zinc-500">Trading days</span><span>{fmtN(c.trading_days)}</span>
              <span className="text-zinc-500">Underlying candles</span><span data-testid={`ql-ucount-${c.symbol}`}>{fmtN(c.underlying_candles)}</span>
              <span className="text-zinc-500">Option candles</span><span>{fmtN(c.option_candles)}</span>
              <span className="text-zinc-500">Contracts</span><span>{fmtN(c.contracts)}</span>
              <span className="text-zinc-500">Next expiry</span><span className="text-[#22C55E]">{c.next_expiry}</span>
            </div>
            <div className="text-[10px] font-mono text-zinc-600">
              Last candle: {c.last_ts ? new Date(c.last_ts).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) + " IST" : "—"}
            </div>
          </div>
        ))}
      </div>

      <div className="border border-zinc-800 bg-[#0a0a0a] p-4 flex items-start gap-3 text-xs text-zinc-400 font-mono">
        <Database className="h-4 w-4 text-[#22C55E] shrink-0 mt-0.5" />
        <div>
          Phase 1 scope: instrument master · immutable candle store · CSV/Excel import with mapping + validation ·
          ATM/chain reconstruction · data quality. Analytics engines (volatility, regimes, premium response, backtesting,
          edge finder) unlock in later phases once real historical data is loaded. Nothing on this page is simulated —
          empty means empty.
        </div>
      </div>
    </div>
  );
};

export default QLDashboard;
